import React, { useRef } from "react";
import {
  TournamentActivity,
  UserActivities,
  ActivityStatus,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { UseInfiniteQueryResult } from "@tanstack/react-query";

import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import Skeleton from "../../Skeleton/Skeleton";
import SkeletonList from "../../SkeletonList/SkeletonList";
import { ThemeVariant } from "../../../utils/constants/generic-types";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import { useTranslation } from "react-i18next";
import { getMetadataValue } from "../../../utils/helpers";

import ImageFallbackThumbnail from "../../../images/fallback-thumbnail.png";
import DefaultAvatar from "../../../images/fallback-avatar.png";
import "./ResultHistorySection.scss";

const STATUS_LABEL_KEYS = {
  ongoing: "results_page.status_ongoing",
  won: "results_page.status_won",
  tie: "results_page.status_tie",
  second: "results_page.status_second",
  cancelled: "results_page.status_cancelled",
  reveal: "results_page.status_reveal",
} as const;

type StatusVariant = keyof typeof STATUS_LABEL_KEYS;

function getStatusVariant(activity: TournamentActivity): StatusVariant {
  if (activity.status === ActivityStatus.PENDING) return "ongoing";
  if (getMetadataValue(activity, "subStatus") === "CANCELLED")
    return "cancelled";
  if (!activity.viewed) return "reveal";
  if (getMetadataValue(activity, "isTie") === "true") return "tie";
  return getMetadataValue(activity, "isWin") === "true" ? "won" : "second";
}

const SKELETON_SHIMMER_STYLE = { background: "#1b0c3dff" };
const SKELETON_BG_STYLE = { background: "rgba(51, 23, 120, 0.1)" };

function ResultCard({
  activity,
  onClick,
  onReveal,
}: {
  activity: TournamentActivity;
  onClick?: () => void;
  onReveal?: (activity: TournamentActivity) => void;
}) {
  const { t } = useTranslation("common");
  const variant = getStatusVariant(activity);
  const opponentUsername = getMetadataValue(activity, "opponentUsername");
  const opponentAvatarUrl = getMetadataValue(activity, "opponentAvatarUrl");

  return (
    <div className="result-card" onClick={onClick} role="button" tabIndex={0}>
      <img
        className="card-image"
        src={activity.image || ImageFallbackThumbnail}
        alt={activity.title}
      />
      <div className="card-info">
        <p className="card-title">
          {getMetadataValue(activity, "gameName") || activity.title}
        </p>
        <p className="card-subtitle">
          {t("games:versus")}
          <img
            className="opponent-avatar"
            src={opponentAvatarUrl || DefaultAvatar}
            alt=""
            onError={(e) => {
              e.currentTarget.src = DefaultAvatar;
            }}
          />
          {opponentUsername || t("games:player")}
        </p>
      </div>
      <div className="card-status">
        {variant === "reveal" ? (
          <PrimaryActionButton
            text={t(STATUS_LABEL_KEYS.reveal)}
            buttonClassName="reveal-btn"
            onClick={(e) => {
              e.stopPropagation();
              onReveal?.(activity);
            }}
          />
        ) : (
          <div className={`status-${variant}`}>
            <span>{t(STATUS_LABEL_KEYS[variant])}</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface ResultHistorySectionProps {
  title: string;
  records: TournamentActivity[];
  query: Pick<
    UseInfiniteQueryResult,
    "hasNextPage" | "isFetchingNextPage" | "fetchNextPage"
  >;
  onActivityClick?: (activity: TournamentActivity) => void;
  onReveal?: (activity: TournamentActivity) => void;
}

export default function ResultHistorySection({
  title,
  records,
  query,
  onActivityClick,
  onReveal,
}: ResultHistorySectionProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver({
    targetRef: bottomRef,
    options: { threshold: 0.1 },
    callback: ([entry]) => {
      if (entry.isIntersecting && query.hasNextPage) {
        query.fetchNextPage();
      }
    },
  });

  if (records.length === 0) return null;

  return (
    <div className="result-history-section">
      <h2 className="section-heading">{title}</h2>
      <div className="section-list">
        {records.map((activity) => (
          <ResultCard
            key={activity.referenceId}
            activity={activity}
            onClick={
              onActivityClick ? () => onActivityClick(activity) : undefined
            }
            onReveal={onReveal}
          />
        ))}
        <div ref={bottomRef} />
        {query.isFetchingNextPage && (
          <div className="d-flex justify-content-center">
            <CustomSpinner size="sm" isPrimary />
          </div>
        )}
      </div>
    </div>
  );
}

export function StaticResultHistorySection({
  title,
  activities,
}: {
  title: string;
  activities: UserActivities[];
}) {
  if (activities.length === 0) return null;

  return (
    <div className="result-history-section">
      <h2 className="section-heading">{title}</h2>
      <div className="section-list">
        {activities.map((activity) => (
          <ResultCard
            key={activity.referenceId}
            activity={activity as TournamentActivity}
          />
        ))}
      </div>
    </div>
  );
}

export function ResultHistorySectionSkeleton() {
  return (
    <div className="result-history-section">
      <Skeleton
        width={100}
        height={16}
        borderRadius={4}
        shimmerStyle={SKELETON_SHIMMER_STYLE}
        style={{ ...SKELETON_BG_STYLE, marginBottom: "0.5rem" }}
        theme={ThemeVariant.DARK}
      />
      <div className="section-list">
        <SkeletonList noOfItems={5}>
          <div className="result-card-skeleton">
            <Skeleton
              width={40}
              height={40}
              borderRadius={12}
              shimmerStyle={SKELETON_SHIMMER_STYLE}
              style={SKELETON_BG_STYLE}
              theme={ThemeVariant.DARK}
            />
            <div className="card-info-skeleton">
              <Skeleton
                width="60%"
                height={14}
                borderRadius={4}
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
              <Skeleton
                width="40%"
                height={12}
                borderRadius={4}
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
            </div>
            <Skeleton
              width={80}
              height={32}
              borderRadius={8}
              shimmerStyle={SKELETON_SHIMMER_STYLE}
              style={SKELETON_BG_STYLE}
              theme={ThemeVariant.DARK}
            />
          </div>
        </SkeletonList>
      </div>
    </div>
  );
}
